<template>
  <!-- min-h-dvh over min-h-screen: on mobile 100vh counts the space behind the
       browser's address bar, so the layout resized every time that bar hid or
       reappeared while scrolling. min-h-screen stays as the fallback for
       browsers without dvh. -->
  <div id="app" :class="{ 'dark': isDarkMode }" class="min-h-screen min-h-dvh bg-background-primary dark:bg-background-primary-dark text-text-primary dark:text-text-primary-dark flex flex-col">
    <OnboardingPrompt
      v-if="isLoggedIn && isAdmin"
      :tenant-id="currentTenantId"
      :tenant-name="currentTenantName"
    />

    <ToastNotification
      v-if="showNotification"
      :type="notification.type"
      :message="notification.message"
      :duration="notification.duration"
      :visible="showNotification"
      @close="handleNotificationClose"
    />

    <div class="flex flex-1 relative">
      <!-- Mobile overlay -->
      <div v-if="sidebarOpen && isLoggedIn" class="fixed inset-0 bg-black/50 z-30 lg:hidden" @click="sidebarOpen = false"></div>

      <!-- Sidebar -->
      <aside v-if="isLoggedIn"
        class="fixed lg:static inset-y-0 left-0 z-40 w-64 max-w-[85vw] bg-background-primary dark:bg-background-primary-dark border-r border-border dark:border-border-dark flex flex-col transform transition-transform duration-200 lg:translate-x-0 overflow-y-auto overscroll-contain"
        :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'">

        <div class="p-4 flex items-center justify-between border-b border-border dark:border-border-dark">
          <h2 class="text-lg font-bold text-primary dark:text-primary-light">S2C Admin</h2>
          <button @click="sidebarOpen = false" class="lg:hidden p-1 rounded text-text-secondary dark:text-text-secondary-dark hover:bg-background-secondary dark:hover:bg-background-secondary-dark">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <nav class="flex-1 p-3 space-y-1">
          <router-link to="/" class="nav-link" @click="closeMobile">
            <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
            Your Tenants
          </router-link>

          <!-- Content — visible to view / edit / admin -->
          <div v-if="canView" class="nav-section">
            <button @click="toggleSection('content')" class="nav-section-header">
              <span>Content</span>
              <svg class="w-4 h-4 transition-transform" :class="openSections.content ? 'rotate-90' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
            <div v-show="openSections.content" class="nav-section-items">
              <router-link :to="`/tenants/${tid}/documents`" class="nav-link" @click="closeMobile">Documents</router-link>
              <router-link :to="`/tenants/${tid}/updates`" class="nav-link" @click="closeMobile">Updates</router-link>
              <router-link :to="`/tenants/${tid}/compliances`" class="nav-link" @click="closeMobile">Compliances</router-link>
              <router-link :to="`/tenants/${tid}/security-categories`" class="nav-link" @click="closeMobile">Security Categories</router-link>
            </div>
          </div>

          <!-- Products & Security — all roles including uploader -->
          <div v-if="canView || isUploader" class="nav-section">
            <button @click="toggleSection('products')" class="nav-section-header">
              <span>Products &amp; Security</span>
              <svg class="w-4 h-4 transition-transform" :class="openSections.products ? 'rotate-90' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
            <div v-show="openSections.products" class="nav-section-items">
              <router-link :to="`/tenants/${tid}/products`" class="nav-link" @click="closeMobile">Products</router-link>
              <router-link v-if="canView" :to="`/tenants/${tid}/portfolio`" class="nav-link" @click="closeMobile">SBOM Portfolio</router-link>
              <router-link v-if="canView" :to="`/tenants/${tid}/vulnerabilities`" class="nav-link" @click="closeMobile">Vulnerabilities</router-link>
              <router-link v-if="canView" :to="`/tenants/${tid}/vuln-reports`" class="nav-link" @click="closeMobile">Vuln Reports</router-link>
            </div>
          </div>

          <!-- Regulatory Compliance — view / edit / admin -->
          <div v-if="canView" class="nav-section">
            <button @click="toggleSection('cra')" class="nav-section-header">
              <span>Regulatory Compliance</span>
              <svg class="w-4 h-4 transition-transform" :class="openSections.cra ? 'rotate-90' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
            <div v-show="openSections.cra" class="nav-section-items">
              <router-link :to="`/tenants/${tid}/cra-readiness`" class="nav-link" @click="closeMobile">CRA Readiness</router-link>
              <router-link :to="`/tenants/${tid}/nis2-readiness`" class="nav-link" @click="closeMobile">NIS2 Readiness</router-link>
              <router-link :to="`/tenants/${tid}/dora-readiness`" class="nav-link" @click="closeMobile">DORA Readiness</router-link>
              <router-link :to="`/tenants/${tid}/eo14028-readiness`" class="nav-link" @click="closeMobile">US EO 14028</router-link>
              <router-link :to="`/tenants/${tid}/incidents`" class="nav-link" @click="closeMobile">Incidents</router-link>
              <router-link :to="`/tenants/${tid}/enisa-reporting`" class="nav-link" @click="closeMobile">ENISA Reporting</router-link>
            </div>
          </div>

          <!-- Administration — admin only -->
          <div v-if="isAdmin" class="nav-section">
            <button @click="toggleSection('admin')" class="nav-section-header">
              <span>Administration</span>
              <svg class="w-4 h-4 transition-transform" :class="openSections.admin ? 'rotate-90' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
            <div v-show="openSections.admin" class="nav-section-items">
              <router-link :to="`/tenants/${tid}/users`" class="nav-link" @click="closeMobile">Users</router-link>
              <router-link :to="`/tenants/${tid}/document-access`" class="nav-link" @click="closeMobile">Access Requests</router-link>
              <router-link :to="`/tenants/${tid}/customer-access`" class="nav-link" @click="closeMobile">Customer Access</router-link>
              <router-link :to="`/tenants/${tid}/subscribers`" class="nav-link" @click="closeMobile">Subscribers</router-link>
              <router-link :to="`/tenants/${tid}/settings`" class="nav-link" @click="closeMobile">Settings</router-link>
              <router-link :to="`/tenants/${tid}/service-accounts`" class="nav-link" @click="closeMobile">API Tokens</router-link>
              <router-link :to="`/tenants/${tid}/webhooks`" class="nav-link" @click="closeMobile">Webhooks</router-link>
              <router-link :to="`/tenants/${tid}/audit-logs`" class="nav-link" @click="closeMobile">Audit Logs</router-link>
            </div>
          </div>
        </nav>
      </aside>

      <!-- Main content -->
      <div class="flex-1 flex flex-col min-w-0">
        <header v-if="isLoggedIn" class="bg-background-primary dark:bg-background-primary-dark border-b border-border dark:border-border-dark px-4 py-3 flex justify-between items-center flex-shrink-0">
          <div class="flex items-center gap-3">
            <button @click="sidebarOpen = !sidebarOpen" class="lg:hidden p-1.5 rounded-md text-text-secondary dark:text-text-secondary-dark hover:bg-background-secondary dark:hover:bg-background-secondary-dark">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
            </button>
            <h1 class="text-lg font-bold text-primary dark:text-primary-light truncate">{{ currentTenantName }}</h1>
          </div>
          <div class="flex items-center space-x-2">
            <a
              v-if="publicSiteUrl"
              :href="publicSiteUrl"
              target="_blank"
              rel="noopener noreferrer"
              :title="`Open your live trust center — ${publicSiteUrl}`"
              class="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border dark:border-border-dark text-text-secondary dark:text-text-secondary-dark hover:bg-background-secondary dark:hover:bg-background-secondary-dark transition-colors text-sm"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
              <span class="hidden sm:inline">View live site</span>
            </a>
            <button @click="toggleDarkMode" class="p-2 rounded-full bg-background-secondary dark:bg-background-secondary-dark text-text-secondary dark:text-text-secondary-dark hover:bg-background-tertiary dark:hover:bg-background-tertiary-dark transition-colors text-sm">
              {{ isDarkMode ? '☀' : '☾' }}
            </button>
            <button @click="logout" class="btn-primary text-sm">Logout</button>
          </div>
        </header>
        <!-- No overflow-y-auto here. Its height is content-driven, so it never
             scrolled independently on desktop, but on mobile it still registered
             as a second scroll container nested inside the page's own, which is
             what made momentum scrolling stutter. -->
        <main class="flex-1 p-4 md:p-8">
          <router-view></router-view>
        </main>

        <footer class="bg-background-primary dark:bg-background-primary-dark border-t border-border dark:border-border-dark flex-shrink-0">
          <div class="container mx-auto px-4 md:px-8 py-3">
            <div class="flex flex-col sm:flex-row items-center justify-between gap-1">
              <p class="text-xs text-text-secondary dark:text-text-secondary-dark">
                Powered by <a href="https://security2center.com" target="_blank" rel="noopener noreferrer" class="text-primary dark:text-primary-light hover:underline font-medium">s2c</a>
              </p>
              <p class="text-xs text-text-secondary dark:text-text-secondary-dark">Security Compliance Management</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, reactive, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import ToastNotification from './components/Notification.vue'
import OnboardingPrompt from './components/OnboardingPrompt.vue'

// Decode JWT payload without verification (for UI-only role checks).
// The server still enforces roles on every API call.
function decodeJWT(token) {
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return null
  }
}

export default {
  name: 'App',
  components: { ToastNotification, OnboardingPrompt },
  setup() {
    const store = useStore()
    const router = useRouter()
    const isDarkMode = ref(true)
    const sidebarOpen = ref(false)

    const openSections = reactive({
      content: true,
      products: true,
      cra: true,
      admin: false
    })

    onMounted(() => {
      document.documentElement.classList.add('dark')
      document.documentElement.style.colorScheme = 'dark'
    })

    // Freeze the page while the mobile drawer is open. Without this the content
    // behind the scrim scrolled under the drawer, so a swipe aimed at the menu
    // moved the page instead and the drawer appeared to drift.
    watch(sidebarOpen, (open) => {
      document.body.style.overflow = open ? 'hidden' : ''
    })
    // A route change can unmount this with the drawer open; leaving overflow
    // hidden would lock the page for good.
    onUnmounted(() => { document.body.style.overflow = '' })

    const isLoggedIn = computed(() => store.getters.isLoggedIn)
    const tid = computed(() => store.getters.currentTenantId)
    const currentTenantName = computed(() => {
      const t = store.state.tenants.find(t => t.tenant_id === tid.value)
      return t ? t.friendly_name : 'Select a Tenant'
    })

    // Decode the user's role for the current tenant from the stored JWT.
    const currentRole = computed(() => {
      const token = store.state.token
      if (!token || !tid.value) return null
      const claims = decodeJWT(token)
      return claims?.tenant_roles?.[tid.value] ?? null
    })

    // Live public trust center for the active tenant. domain_name is the
    // full hostname (subdomain or custom FQDN), matching how the backend
    // builds tenant URLs.
    const publicSiteUrl = computed(() => {
      const t = store.state.tenants.find(t => t.tenant_id === tid.value)
      return t?.domain_name ? `https://${t.domain_name}` : null
    })

    const isAdmin  = computed(() => currentRole.value === 'admin')
    const canEdit  = computed(() => ['admin', 'edit'].includes(currentRole.value))
    const canView  = computed(() => ['admin', 'edit', 'view'].includes(currentRole.value))
    // uploader: only Products & Security
    const isUploader = computed(() => currentRole.value === 'uploader')

    const notification = computed(() => store.getters['notification/currentNotification'])
    const showNotification = computed(() => !!notification.value)
    const handleNotificationClose = () => store.dispatch('notification/clearNotification')

    const logout = () => { store.dispatch('logout'); router.push('/login') }

    const toggleDarkMode = () => {
      isDarkMode.value = !isDarkMode.value
      document.documentElement.classList.toggle('dark', isDarkMode.value)
      document.documentElement.style.colorScheme = isDarkMode.value ? 'dark' : 'light'
    }

    const toggleSection = (section) => { openSections[section] = !openSections[section] }
    const closeMobile = () => { sidebarOpen.value = false }

    return {
      isLoggedIn, logout, isDarkMode, toggleDarkMode,
      tid, currentTenantName, currentTenantId: tid, publicSiteUrl,
      isAdmin, canEdit, canView, isUploader,
      notification, showNotification, handleNotificationClose,
      sidebarOpen, openSections, toggleSection, closeMobile
    }
  }
}
</script>

<style>
@import './styles/main.css';

:root { color-scheme: dark; }
.dark { color-scheme: dark; }

/* The dark-mode overrides that used to live here set background-color and
   color from --bg-primary-dark, --text-primary-dark and friends, none of which
   were ever defined. An undefined custom property is invalid at computed-value
   time, so background-color fell back to its initial value — transparent — and
   these rules outranked Tailwind's own dark utilities on specificity
   (.dark .bg-background-primary beats .bg-background-primary).

   Since dark mode is always on, the sidebar, header and footer were painted
   transparent. On desktop that is easy to miss because the sidebar sits beside
   the content, but on mobile it slides over it, so page text showed straight
   through the open menu. Every element carrying bg-background-primary also
   carries dark:bg-background-primary-dark, so deleting these leaves Tailwind to
   do the job correctly. */

.nav-link {
  @apply flex items-center gap-2 py-2 px-3 text-sm text-text-secondary dark:text-text-secondary-dark
         hover:bg-background-secondary dark:hover:bg-background-secondary-dark rounded-md transition-colors;
}
.nav-link.router-link-active {
  @apply bg-background-secondary dark:bg-background-secondary-dark text-primary dark:text-primary-light font-medium;
}
.nav-icon { @apply w-4 h-4 flex-shrink-0; }
.nav-section { @apply mt-2; }
.nav-section-header {
  @apply w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold uppercase tracking-wider
         text-text-secondary dark:text-text-secondary-dark hover:text-text-primary dark:hover:text-text-primary-dark
         transition-colors cursor-pointer;
}
.nav-section-items { @apply ml-1 space-y-0.5 mt-0.5; }
</style>
